import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import MenuPage from './MenuPage';
import CartPage from './CartPage';
import OrdersPage from './OrdersPage';
import FavoritesPage from './FavoritesPage';
import NotificationsPage from './NotificationsPage';
import ProfilePage from './ProfilePage';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import TermsOfServicePage from './TermsOfServicePage';
import LiabilityDisclaimerPage from './LiabilityDisclaimerPage';
import UserGuidePage from './UserGuidePage';
import RestaurantDetailPage from './RestaurantDetailPage';
import FeaturedItemsPage from './FeaturedItemsPage';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import TabBar from '../components/ios/TabBar';
import { useAuth } from '../contexts/AuthContext';
import { useFirebase } from '../contexts/FirebaseContext';
import { useNotifications } from '../contexts/NotificationsContext';
import { isActiveOrder } from '../utils/orderStatus';
import { UtensilsCrossed, Clock, Heart, Bell, User } from 'lucide-react';

// iOS CustomerDashboardView TabView — 5 tabs, red tint:
// Menu (fork.knife) · Orders (clock.fill) · Favorites (heart.fill)
// · Notifications (bell.fill, unread badge) · Profile (person.fill)
// Cart is NOT a tab — iOS shows a floating cart button + sheet inside MenuView.
function CustomerDashboard() {
  const location = useLocation();
  const { currentUser, loading: authLoading } = useAuth();
  const { db } = useFirebase();
  const { unreadCount } = useNotifications();
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [activeOrderCount, setActiveOrderCount] = useState(0);

  // iOS: .badge(activeOrders.count) on the Orders tab — live count of
  // in-flight orders so the customer sees it from any tab.
  useEffect(() => {
    if (!currentUser) {
      setActiveOrderCount(0);
      return;
    }
    const q = query(collection(db, 'orders'), where('customerId', '==', currentUser.uid));
    return onSnapshot(q, (snapshot) => {
      setActiveOrderCount(snapshot.docs.filter((d) => isActiveOrder(d.data())).length);
    });
  }, [currentUser, db]);

  const isPublicRoute = location.pathname === '/dashboard/menu' || location.pathname === '/dashboard';

  // Wait for auth state to resolve before redirecting
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center px-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-red-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser && !isGuestMode && !isPublicRoute) {
    return <Navigate to="/dashboard/menu" replace />;
  }

  if (!currentUser && !isGuestMode) {
    setIsGuestMode(true);
    return null;
  }

  const tabs = [
    { to: '/dashboard/menu',          label: 'Menu',          Icon: UtensilsCrossed },
    { to: '/dashboard/orders',        label: 'Orders',        Icon: Clock,  protected: true, badge: activeOrderCount },
    { to: '/dashboard/favorites',     label: 'Favorites',     Icon: Heart,  protected: true },
    { to: '/dashboard/notifications', label: 'Notifications', Icon: Bell,   protected: true, badge: unreadCount },
    { to: '/dashboard/profile',       label: 'Profile',       Icon: User,   protected: true },
  ];

  return (
    <>
      <SEO />
      <div className="flex flex-col h-screen bg-[#F2F2F7]">
        {/* Main content — leaves room for the fixed bottom tab bar */}
        <div className="flex-1 overflow-y-auto pb-20">
          <Routes>
            <Route path="/menu" element={<MenuPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/orders" element={currentUser ? <OrdersPage /> : <Navigate to="/login" replace />} />
            <Route path="/favorites" element={currentUser ? <FavoritesPage /> : <Navigate to="/login" replace />} />
            <Route path="/notifications" element={currentUser ? <NotificationsPage /> : <Navigate to="/login" replace />} />
            <Route path="/profile" element={currentUser ? <ProfilePage /> : <Navigate to="/login" replace />} />
            <Route path="/restaurant/:restaurantId" element={<RestaurantDetailPage />} />
            <Route path="/featured" element={<FeaturedItemsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/liability" element={<LiabilityDisclaimerPage />} />
            <Route path="/help" element={<UserGuidePage />} />
            <Route path="/support" element={<UserGuidePage />} />
            <Route path="/" element={<Navigate to="/dashboard/menu" replace />} />
          </Routes>
          {/* Footer with location text for SEO */}
          <Footer />
        </div>

        {/* iOS TabView equivalent */}
        <TabBar tabs={tabs} />
      </div>
    </>
  );
}

export default CustomerDashboard;
